import type { EnhancedStore } from '@reduxjs/toolkit';
import { Middleware } from 'redux';
import { RootState } from '@spa/lib/store/RootState';
import authReducer from './auth/authSlice';
import cartsReducer from './carts/cartsSlice';
import { configureStore } from '@reduxjs/toolkit';
import contactReducer from './contact/ContactSlice';
import cookieConsentsReducer from './cookieConsents/cookieConsentsSlice';
import createSagaMiddleware from '@redux-saga/core';
import experimentsReducer from './experiments/experimentsSlice';
import logger from '../logger';
import publicFeatureLogStoreChanges from '../configs/publicFeatureLogStoreChanges';
import routerReducer from './router/routerSlice';
import saga from './sagas/saga';
import sanitizeAction from './sanitizeAction';
import searchReducer from './search/searchSlice';
import sessionReducer from './session/sessionSlice';
import settingsReducer from './settings/settingsSlice';
import tasksReducer from './tasks/tasksSlice';
import userReducer from './user/userSlice';
import wishlistsReducer from '@spa/lib/store/wishlists/wishlistsSlice';

const devtoolsEnabled =
  (process.env.NEXT_PUBLIC_FEATURE_REDUX_DEVTOOLS_ENABLED?.toString() ?? '').toLowerCase() ===
  'true';

const logToConsoleMiddleware: Middleware = () => (next) => (action) => {
  logger.info(sanitizeAction(action), 'Redux Action');
  return next(action);
};

export const makeStore = (
  enableSagas = true,
  preloadedState?: Partial<RootState>
): EnhancedStore => {
  const sagaMiddleware = createSagaMiddleware();

  const store = configureStore({
    reducer: {
      carts: cartsReducer,
      tasks: tasksReducer,
      router: routerReducer,
      settings: settingsReducer,
      auth: authReducer,
      cookieConsents: cookieConsentsReducer,
      contact: contactReducer,
      search: searchReducer,
      session: sessionReducer,
      user: userReducer,
      wishlists: wishlistsReducer,
      experiments: experimentsReducer
    },
    devTools: devtoolsEnabled && { actionSanitizer: sanitizeAction },
    middleware: (getDefaultMiddleware) => {
      const additionalMiddlewares: Array<Middleware> = [sagaMiddleware];
      if (publicFeatureLogStoreChanges) {
        additionalMiddlewares.push(logToConsoleMiddleware);
      }
      return getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: false,
        thunk: false
      }).concat(additionalMiddlewares);
    },
    preloadedState: preloadedState as RootState | undefined
  });

  if (enableSagas) {
    sagaMiddleware.run(saga);
  }

  if (publicFeatureLogStoreChanges) {
    let previousState = store.getState();
    store.subscribe(() => {
      const nextState = store.getState();
      const changedSliceKeys = (Object.keys(nextState) as Array<keyof RootState>).filter(
        (sliceKey) => nextState[sliceKey] !== previousState[sliceKey]
      );
      previousState = nextState;
      logger.info({ store: { changedSliceKeys } }, 'Store changed.');
    });
  }

  return store;
};
